/**
 * Help Center — /business/help-center
 * Searchable self-serve knowledge base with category cards and popular articles.
 */
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Rocket,
  Layers,
  Video,
  Plug,
  CreditCard,
  ShieldCheck,
  ChevronRight,
  MessageSquare,
  BookOpen,
  ArrowRight,
  LifeBuoy,
} from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

/* ─────────────────────────────── data ──────────────────────────────────── */

interface HelpCategory {
  icon: React.ElementType;
  title: string;
  description: string;
  articleCount: number;
  color: string;
  articles: string[];
}

const CATEGORIES: HelpCategory[] = [
  {
    icon: Rocket,
    title: 'Getting Started',
    description: 'Account setup, onboarding checklist, and your first job post.',
    articleCount: 14,
    color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    articles: [
      'Setting up your organisation profile',
      'Inviting team members and assigning roles',
      'Creating your first job posting',
      'Importing candidates via CSV',
      'Configuring email notifications',
    ],
  },
  {
    icon: Layers,
    title: 'Pipeline & Jobs',
    description: 'Manage stages, SLAs, custom fields, and bulk actions.',
    articleCount: 21,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
    articles: [
      'Creating custom pipeline stages',
      'Setting stage-level SLAs',
      'Using bulk actions on candidates',
      'Advanced candidate filters',
      'Archiving and re-opening jobs',
    ],
  },
  {
    icon: Video,
    title: 'AI Interviews',
    description: 'Video & voice screening setup, rubric builder, and reviewing results.',
    articleCount: 18,
    color: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300',
    articles: [
      'Sending an AI video interview invite',
      'Building a custom evaluation rubric',
      'Understanding AI role-fit scores',
      'Reviewing video transcripts',
      'Re-sending expired interview links',
    ],
  },
  {
    icon: Plug,
    title: 'Integrations',
    description: 'Connect HRIS tools, job boards, Zapier, and the REST API.',
    articleCount: 12,
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
    articles: [
      'Connecting LinkedIn Recruiter',
      'Zapier integration walkthrough',
      'Configuring Workday or BambooHR',
      'Using the REST API: authentication',
      'Posting jobs to Indeed & Naukri',
    ],
  },
  {
    icon: CreditCard,
    title: 'Billing & Plans',
    description: 'Subscription management, invoices, seats, and upgrades.',
    articleCount: 9,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    articles: [
      'Upgrading or downgrading your plan',
      'Adding or removing seats',
      'Downloading invoices',
      'Understanding usage limits',
      'Cancellation and data retention',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Security & Compliance',
    description: 'GDPR, data residency, SSO, audit logs, and DPA requests.',
    articleCount: 11,
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
    articles: [
      'Enabling SAML / SSO',
      'Requesting a Data Processing Agreement',
      'Configuring data retention policies',
      'Submitting a data subject access request',
      'Reviewing the audit log',
    ],
  },
];

const POPULAR: { title: string; category: string; views: string }[] = [
  { title: 'How do I reset my password?', category: 'Getting Started', views: '4.2k views' },
  { title: 'Why did an AI interview link expire?', category: 'AI Interviews', views: '3.8k views' },
  { title: 'How is the role-fit score calculated?', category: 'AI Interviews', views: '3.1k views' },
  { title: 'Can I import candidates from my old ATS?', category: 'Getting Started', views: '2.9k views' },
  { title: 'How do I connect GoGrabJob to LinkedIn?', category: 'Integrations', views: '2.7k views' },
  { title: 'Where can I download my invoice?', category: 'Billing & Plans', views: '2.4k views' },
  { title: 'How do I add a team member?', category: 'Getting Started', views: '2.1k views' },
  { title: 'What happens to my data after cancellation?', category: 'Billing & Plans', views: '1.9k views' },
];

/* ─────────────────────────────── components ─────────────────────────────── */

function CategoryCard({ cat, idx }: { cat: HelpCategory; idx: number }) {
  const [open, setOpen] = useState(false);
  const Icon = cat.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: 0.06 * idx }}
      className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
    >
      <button
        className="flex w-full items-start gap-4 p-6 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${cat.color}`}>
          <Icon className="h-5 w-5" aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-violet-700 dark:text-zinc-50 dark:group-hover:text-violet-400">
              {cat.title}
            </h3>
            <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
              {cat.articleCount} articles
            </span>
          </div>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-zinc-400">{cat.description}</p>
        </div>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 ${open ? 'rotate-90' : ''} mt-1`}
          aria-hidden
        />
      </button>

      {open && (
        <ul className="border-t border-gray-100 px-6 pb-4 dark:border-zinc-800">
          {cat.articles.map((title) => (
            <li key={title}>
              <a
                href="#"
                className="flex items-center gap-2 py-2.5 text-sm text-gray-600 hover:text-violet-700 dark:text-zinc-400 dark:hover:text-violet-400 transition-colors"
              >
                <BookOpen className="h-3.5 w-3.5 shrink-0 text-gray-300 dark:text-zinc-600" />
                {title}
              </a>
            </li>
          ))}
          <li>
            <a
              href="#"
              className="mt-1 flex items-center gap-1 text-xs font-semibold text-violet-600 hover:underline dark:text-violet-400"
            >
              View all {cat.articleCount} articles <ArrowRight className="h-3 w-3" />
            </a>
          </li>
        </ul>
      )}
    </motion.div>
  );
}

/* ────────────────────────────── page ───────────────────────────────────── */

export function HelpCenterPage() {
  const [query, setQuery] = useState('');

  const filteredPopular = query.trim()
    ? POPULAR.filter(
        (a) =>
          a.title.toLowerCase().includes(query.toLowerCase()) ||
          a.category.toLowerCase().includes(query.toLowerCase()),
      )
    : POPULAR;

  return (
    <B2BMarketingPageLayout>

      {/* ── Hero / Search ── */}
      <section
        className="relative overflow-hidden px-4 pb-14 pt-14 sm:px-6 md:px-8 md:pt-20"
        style={{ background: 'linear-gradient(150deg, #F5F3FF 0%, #EDE9FE 40%, #F5F3FF 65%, #EEF2FF 100%)' }}
      >
        <div className="relative z-10 mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100">
            <LifeBuoy className="h-6 w-6 text-violet-600" aria-hidden />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            How can we help?
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-gray-500">
            Search our knowledge base or browse by topic below.
          </p>

          {/* search bar */}
          <div className="relative mx-auto mt-8 max-w-lg">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search help articles…"
              className="w-full rounded-xl border border-gray-200 bg-white py-3.5 pl-12 pr-4 text-sm text-gray-900 shadow-sm outline-none placeholder-gray-400 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20"
            />
          </div>

          {/* quick links */}
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {['Password reset', 'Import candidates', 'AI interview', 'Billing', 'API docs'].map((t) => (
              <button
                key={t}
                onClick={() => setQuery(t)}
                className="rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-xs font-medium text-gray-600 shadow-sm hover:border-violet-300 hover:text-violet-700 transition-colors"
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main content ── */}
      <section className="px-4 py-14 sm:px-6 md:px-8">
        <div className="mx-auto max-w-5xl space-y-16">

          {/* categories */}
          <div>
            <h2 className="mb-6 text-[11px] font-semibold uppercase tracking-[0.13em] text-violet-600 dark:text-violet-400">
              Browse by topic
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {CATEGORIES.map((cat, i) => (
                <CategoryCard key={cat.title} cat={cat} idx={i} />
              ))}
            </div>
          </div>

          {/* popular articles */}
          <div>
            <h2 className="mb-6 text-[11px] font-semibold uppercase tracking-[0.13em] text-violet-600 dark:text-violet-400">
              {query ? `Results for "${query}"` : 'Popular articles'}
            </h2>
            {filteredPopular.length > 0 ? (
              <div className="divide-y divide-gray-100 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:divide-zinc-800 dark:border-zinc-800 dark:bg-zinc-900">
                {filteredPopular.map((article, i) => (
                  <motion.a
                    key={article.title}
                    href="#"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.03 * i }}
                    className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-gray-50 dark:hover:bg-zinc-800/60 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-zinc-100">{article.title}</p>
                        <p className="text-xs text-gray-400 dark:text-zinc-500">{article.category}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="hidden text-[11px] text-gray-400 sm:block">{article.views}</span>
                      <ChevronRight className="h-4 w-4 text-gray-300 dark:text-zinc-600" />
                    </div>
                  </motion.a>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-gray-100 bg-white py-16 text-center dark:border-zinc-800 dark:bg-zinc-900">
                <p className="text-sm text-gray-400">No articles matched your search.</p>
              </div>
            )}
          </div>

          {/* Contact support CTA */}
          <div className="flex flex-col items-center gap-6 rounded-2xl border border-violet-100 bg-violet-50 p-8 text-center sm:flex-row sm:text-left dark:border-violet-900/30 dark:bg-violet-950/20">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-violet-100 dark:bg-violet-900/40">
              <MessageSquare className="h-7 w-7 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-bold text-gray-900 dark:text-zinc-50">
                Can't find what you need?
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
                Our support team is online Monday – Friday, 9 am – 7 pm IST. Enterprise customers have 24/7 priority access.
              </p>
            </div>
            <div className="flex flex-col gap-2.5 sm:flex-row">
              <a
                href="/business/contact-us"
                className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 transition-colors"
              >
                <MessageSquare className="h-4 w-4" />
                Contact support
              </a>
              <a
                href="/business/faq"
                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-5 py-2.5 text-sm font-semibold text-violet-700 hover:bg-violet-50 transition-colors dark:border-violet-800 dark:bg-zinc-900 dark:text-violet-400 dark:hover:bg-zinc-800"
              >
                View FAQ
              </a>
            </div>
          </div>

        </div>
      </section>
    </B2BMarketingPageLayout>
  );
}
